<script>
/**
 * ドライバー設定モーダル
 */
import {mapActions} from "vuex";

export default {
  name: "DriverSettingModal",
  components: {},
  props: {
    /**
     * モーダル表示設定
     * @default false
     */
    enableDialog: {
      type: Boolean,
      default: () => false,
    },
    /**
     * 受注ID
     * @default null
     */
    orderId: {
      type: Number,
      default: () => null,
    },
    /**
     * ドライバースケジュールID
     * @default null
     */
    driverScheduleId: {
      type: Number,
      default: () => null,
    },
    /**
     * ドライバースケジュール詳細ID
     * @default null
     */
    driverScheduleDetailId: {
      type: Number,
      default: null,
    },
    /**
     * ドライバー一覧
     * @default []
     */
    drivers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      /**
       * オーダー情報
       * @param order { Object }
       * @property order.plan_date { String } 運搬日
       * @property order.use_approval_btn { Boolean } 承認ボタン使用フラグ
       * @property order.created_date { String } 作成日
       * @property order.order_collection_id { Number } 受注コレクションID
       * @property order.item_names { Array } 品目名
       */
      order: null,
      /** 車両情報 */
      vehicles: [],
    }
  },
  computed: {
    minCollectedDate () {
        return this.$dayjs(this.order.created_date).subtract(10, 'years').format('YYYY-MM-DD')
    },
    maxCollectedDate () {
      return this.$dayjs(this.order.created_date).add(5, 'years').format("YYYY-MM-DD")
    },
    /**
     * 登録ボタンのテキストを取得
     */
    registerButtonText () {
      return this.order.use_approval_btn ? '排出申請' : '登録'
    },
  },
  watch: {
    /**
     * モーダル表示設定変更監視
     * @param newValue
     */
    enableDialog(newValue) {
      this.order = null;
      this.vehicles = [];
      if (newValue) {
        this.searchOrder();
        this.fetchVehicles({ registered_number: true }).then((content) => {
          this.vehicles = content.vehicles
        })
      }
    },
    'order.plan_date': function (newVal) {
      this.collectedDateChangeEvent(newVal)
    },
  },
  created() {},
  methods: {
    ...mapActions('DriverMasterSearchVehicle', {
      fetchVehicles: 'search',
    }),
    ...mapActions('DriverScheduleDriverSetting', {
      fetchDriverSetting: 'edit',
      updateDriverSetting: 'update',
    }),
    /**
     * 閉じるボタン クリックイベント
     */
    closeButtonClickEvent() {
      this.$emit('closeCallback');
    },
    /**
     * 登録ボタン クリックイベント
     */
    registerButtonClickEvent () {
      this.$refs.observer.validate().then((result) => {
        if(result){
          this.register();
        }
      })
    },
    /**
     * 運搬担当者 チェンジイベント
     * 運搬担当者の名称をセットする.
     * @param user_id
     */
    transportUserChangeEvent (user_id) {
      const transportUser = this.drivers.find( (v) => v.value === user_id)
      this.order.transport_user_name = transportUser?.text
    },
    /**
     * 回収日変更 コールバック
     * @param date { String } 日付
     */
    dateChangedCallback (date) {
      this.order.plan_date = date;
    },
    /**
     * 受注情報を取得
     */
    searchOrder() {
      this.fetchDriverSetting({
        order_id: this.orderId,
      }).then((content) => {
        this.order = content;
      });
    },
    /**
     * 登録処理
     */
    register () {
      // past_manifestをorder_collection_attributesに移動
      const params = { ...this.order };
      if (this.order.order_type === 'collection' && params.past_manifest !== undefined) {
        params.order_collection_attributes = {
          id: this.order.order_collection_id,
          past_manifest: params.past_manifest
        };
        delete params.past_manifest;
      }

      this.updateDriverSetting({
        order_id: this.orderId,
        params: params
      }).then(() => {
        this.$emit('registerCallback');
      });
    },
    collectedDateChangeEvent(item) {
      const now = this.$dayjs(this.$dayjs().format("YYYY-MM-DD"))
      const date = this.$dayjs(item)
      this.order.past_manifest = date.isBefore(now);
    },
  },
}
</script>

<template>
  <v-dialog
    :value="enableDialog"
    width="500"
    scrollable
    @click:outside="closeButtonClickEvent"
    @keydown.esc="closeButtonClickEvent"
    @close="closeButtonClickEvent"
  >
    <validation-observer
      ref="observer"
      v-slot="{ invalid }"
    >
      <v-card v-if="order">
        <v-toolbar
          color="primary"
          dark
          flat
        >
          <v-toolbar-title>運搬設定</v-toolbar-title>
        </v-toolbar>
        <v-card-text style="max-height: 50vh;overflow-y: auto">
          <v-row>
            <v-col>
              <h4>品目: <span class="primary--text">{{ order.item_names.join('・') }}</span></h4>
            </v-col>
          </v-row>

          <v-row dense>
            <v-col v-if="order.can_edit_collected_date">
              <v-checkbox
                v-model="order.past_manifest"
                dense
                label="過去マニフェストの起票"
                hide-details="auto"
                >
              </v-checkbox>
            </v-col>
          </v-row>

          <!-- 回収日 -->
          <v-row style="position: relative;">
            <v-col cols="12">
              <cs-date-picker
                v-if="order.can_edit_collected_date"
                :date.sync="order.plan_date"
                :minimum-date="minCollectedDate"
                :maximum-date="maxCollectedDate"
                @dateChangedCallback="dateChangedCallback"
              >
                <template v-slot="{ on, attrs }">
                  <validation-provider
                    v-slot="{ errors }"
                    :rules="{ required: true, collectedDateCheck: {past_manifest: order.past_manifest} }"
                    name="回収日"
                    class="requiredMark"
                  >
                    <v-text-field
                      v-model="order.plan_date"
                      v-bind="attrs"
                      v-on="on"
                      :error-messages="errors"
                      append-icon="mdi-calendar"
                      clearable
                      dense
                      hide-details="auto"
                      label="回収日"
                      outlined
                      persistent-hint
                      persistent-placeholder
                      readonly
                    />
                  </validation-provider>
                </template>
              </cs-date-picker>
              <div
                v-else
                class="fieldsetLabel"
              >
                <fieldset>
                  <legend>回収日</legend>
                  <span>{{ order.plan_date | date_format }}&nbsp;</span>
                </fieldset>
              </div>
            </v-col>
          </v-row>
          <!-- /回収日 -->

          <!-- 開始時間〜終了時間 -->
          <v-row>
            <v-col cols="12" md="6" class="dialogDateTimePicker">
              <validation-provider
                v-slot="{ errors }"
                :rules="{preferredStartTimeCheck: {preferred_end_time: order.plan_end_time}}"
                name="開始時間"
              >
                <vue-ctk-date-time-picker
                  v-model="order.plan_start_time"
                  no-value-to-custom-elem
                  only-time
                  position="top"
                  color="#0070B3"
                  format="HH:mm"
                  formatted="HH:mm"
                  minute-interval="15"
                >
                  <v-text-field
                    v-model="order.plan_start_time"
                    :error-messages="errors"
                    append-icon="mdi-clock-time-four-outline"
                    clearable
                    dense
                    label="開始時間"
                    outlined
                    hide-details="auto"
                    placeholder="--:--"
                    persistent-placeholder
                    readonly
                  >
                    <template v-slot:append-outer>
                      <div v-if="$vuetify.breakpoint.mdAndUp">
                        〜
                      </div>
                    </template>
                  </v-text-field>
                </vue-ctk-date-time-picker>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6" class="dialogDateTimePicker">
              <vue-ctk-date-time-picker
                v-model="order.plan_end_time"
                no-value-to-custom-elem
                only-time
                position="top"
                color="#0070B3"
                format="HH:mm"
                formatted="HH:mm"
                minute-interval="15"
              >
                <v-text-field
                  v-model="order.plan_end_time"
                  append-icon="mdi-clock-time-four-outline"
                  clearable
                  dense
                  label="終了時間"
                  outlined
                  hide-details="auto"
                  placeholder="--:--"
                  persistent-placeholder
                  readonly
                >
                  <template v-slot:append-outer>
                    <div v-if="$vuetify.breakpoint.mdAndUp">
                      &emsp;
                    </div>
                  </template>
                </v-text-field>
              </vue-ctk-date-time-picker>
            </v-col>
          </v-row>
          <!-- 開始時間〜終了時間 -->

          <!-- 運搬担当者 -->
          <v-row>
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                :vid="`transport_user_name`"
                name="運搬担当者"
              >
                <v-autocomplete
                  v-model="order.transport_user_id"
                  :error-messages="errors"
                  :items="drivers"
                  :return-object="false"
                  :disabled="!order.can_edit_transport_user"
                  clearable
                  dense
                  label="運搬担当者"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  @change="transportUserChangeEvent"
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /運搬担当者 -->

          <!-- 車両番号 -->
          <v-row>
            <v-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                name="車両番号"
                rules="max:15"
              >
                <v-combobox
                  v-model="order.vehicle_number"
                  :error-messages="errors"
                  :items="vehicles"
                  :return-object="false"
                  :clearable="true"
                  :disabled="!order.can_edit_transport_user"
                  item-text="number"
                  item-value="number"
                  dense
                  label="車両番号"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                >
                  <template v-slot:label>
                    <span>車両番号</span>
                    <span class="ml-1">
                        ({{ order.vehicle_number?.length || 0 }} / 15)
                      </span>
                  </template>
                  <template v-slot:counter></template>
                </v-combobox>
              </validation-provider>

            </v-col>
          </v-row>
          <!-- /車両番号 -->
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="closeButtonClickEvent"
          >
            閉じる
          </v-btn>
          <v-btn
            :disabled="invalid"
            color="primary"
            @click="registerButtonClickEvent"
          >
            {{ registerButtonText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </validation-observer>
  </v-dialog>
</template>
